import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight, Award, BarChart3, Bell, BookOpen, Bot, Brain, Calculator, Calendar, ClipboardCheck, Clock, FileText, FolderOpen,
  Globe, GraduationCap, Image, Lightbulb, Megaphone, MessageSquare, Music, Puzzle, Rocket, Shield, Sparkles, Star, Target, Trophy, Users, Video,
} from "lucide-react";
import { API } from "@/App";
import { Card, CardContent } from "@/components/ui/card";
import { RichText } from "@/components/RichText";

export const FEATURE_ICONS = {
  Award, BarChart3, Bell, BookOpen, Bot, Brain, Calculator, Calendar, ClipboardCheck, Clock, FileText, FolderOpen, Globe,
  GraduationCap, Image, Lightbulb, Megaphone, MessageSquare, Music, Puzzle, Rocket, Shield, Sparkles, Star, Target, Trophy, Users, Video,
};

// One card - the whole card is a single link (keyboard + touch friendly); same look as the original design.
export const FeatureCard = ({ card, preview = false }) => {
  const Icon = FEATURE_ICONS[card.icon] || Sparkles;
  const body = (
    <Card className="bg-card text-card-foreground rounded-xl border border-border/50 shadow-sm transition-[box-shadow,transform,border-color] duration-300 h-full group-hover:shadow-lg group-hover:-translate-y-1 group-hover:border-primary/40 group-focus-visible:ring-2 group-focus-visible:ring-primary">
      <CardContent className="p-6 h-full flex flex-col">
        {card.image
          ? <img src={card.image} alt="" className="w-full h-36 object-cover rounded-lg mb-4" data-testid={`feature-card-image-${card.card_id}`} />
          : <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center mb-4 transition-colors group-hover:bg-primary/15"><Icon className="w-6 h-6 text-primary" /></div>}
        <h3 className="text-xl font-semibold text-foreground mb-2 font-['Manrope']">{card.title}</h3>
        <RichText html={(card.description || "").replace(/<\/?a\b[^>]*>/gi, "")} className="text-muted-foreground leading-relaxed flex-1" />
        <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-primary opacity-80 transition-[opacity,gap] group-hover:opacity-100 group-hover:gap-2">
          {card.button_label || "Learn more"} <ArrowRight className="w-4 h-4" />
        </span>
      </CardContent>
    </Card>
  );
  const cls = "group block h-full rounded-xl focus:outline-none";
  const tid = `feature-card-${card.card_id}`;
  if (preview || !card.href) return <div className={cls} data-testid={tid}>{body}</div>;
  if (/^https?:/.test(card.href)) return <a href={card.href} target="_blank" rel="noopener noreferrer" className={cls} aria-label={card.title} data-testid={tid}>{body}</a>;
  if (/^(mailto|tel):/.test(card.href)) return <a href={card.href} className={cls} aria-label={card.title} data-testid={tid}>{body}</a>;
  return <Link to={card.href} className={cls} aria-label={card.title} data-testid={tid}>{body}</Link>;
};

const fadeInUp = { initial: { opacity: 0, y: 20 }, animate: { opacity: 1, y: 0, transition: { duration: 0.5 } } };

export const FeatureCardsGrid = ({ showHeading = true, headingOverride, subheadingOverride }) => {
  const [data, setData] = useState(null);
  useEffect(() => { axios.get(`${API}/website/feature-cards`).then((r) => setData(r.data)).catch(() => setData({ cards: [] })); }, []);
  if (data && !data.cards.length) return null;
  return (
    <div data-testid="feature-cards-section">
      {showHeading && (
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4 font-['Manrope']" data-testid="feature-cards-heading">{headingOverride || data?.heading || "\u00a0"}</h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">{subheadingOverride || data?.subheading}</p>
        </div>
      )}
      <motion.div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" initial="initial" whileInView="animate" viewport={{ once: true }}
        variants={{ animate: { transition: { staggerChildren: 0.1 } } }}>
        {data
          ? data.cards.map((c) => <motion.div key={c.card_id} variants={fadeInUp}><FeatureCard card={c} /></motion.div>)
          : [0, 1, 2].map((i) => <div key={i} className="h-48 rounded-xl bg-muted animate-pulse" />)}
      </motion.div>
    </div>
  );
};
